import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  ReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type NodeChange,
  type ReactFlowInstance,
} from "@xyflow/react";
import {
  Check,
  ChevronRight,
  CloudUpload,
  FolderOpen,
  LoaderCircle,
  PanelLeft,
  Play,
  Redo2,
  Save,
  Undo2,
  X,
} from "lucide-react";
import { api, subscribeToRun } from "../api";
import { AgentNode, type AgentFlowNode } from "./AgentNode";
import { Inspector } from "./Inspector";
import { NodePalette, type NodeTemplate } from "./NodePalette";
import { RunPanel } from "./RunPanel";
import type {
  NodeStatus,
  RunEvent,
  RunRecord,
  RunStatus,
  WorkflowEdge,
  WorkflowGraph,
  WorkflowNode,
  WorkflowNodeData,
  WorkflowSummary,
} from "../../shared/types";

const nodeTypes = { agentNode: AgentNode };

interface BuilderProps {
  workflow: WorkflowSummary;
  onWorkflowUpdate: (workflow: WorkflowSummary) => void;
}

interface GraphSnapshot {
  nodes: AgentFlowNode[];
  edges: Edge[];
}

function cloneSnapshot(nodes: AgentFlowNode[], edges: Edge[]): GraphSnapshot {
  return {
    nodes: structuredClone(nodes),
    edges: structuredClone(edges),
  };
}

function cleanGraph(nodes: AgentFlowNode[], edges: Edge[]): WorkflowGraph {
  return {
    nodes: nodes.map((node) => ({
      id: node.id,
      type: "agentNode",
      position: { x: node.position.x, y: node.position.y },
      data: { ...node.data, status: "idle" },
    })),
    edges: edges.map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: edge.type,
      animated: edge.animated,
    })),
  };
}

function flowNodes(graph: WorkflowGraph): AgentFlowNode[] {
  return graph.nodes.map((node) => ({
    ...node,
    data: { ...node.data, status: "idle" },
  })) as AgentFlowNode[];
}

function flowEdges(graph: WorkflowGraph): Edge[] {
  return graph.edges.map((edge) => ({
    ...edge,
    type: edge.type ?? "smoothstep",
    markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
  }));
}

function dataForTemplate(template: NodeTemplate): WorkflowNodeData {
  const base: WorkflowNodeData = {
    kind: template.kind,
    label: template.label,
    description: template.description,
    active: true,
    status: "idle",
  };
  if (template.kind === "agent" || template.kind === "orchestrator") {
    return {
      ...base,
      provider: "demo",
      modelPolicy: "balanced",
      onError: "stop",
      budget: 1,
      instructions: "Execute esta etapa com precisão e sinalize premissas.",
    };
  }
  return base;
}

export function Builder({ workflow, onWorkflowUpdate }: BuilderProps) {
  const [nodes, setNodes] = useState<AgentFlowNode[]>(() => flowNodes(workflow.graph!));
  const [edges, setEdges] = useState<Edge[]>(() => flowEdges(workflow.graph!));
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  const pastRef = useRef<GraphSnapshot[]>([]);
  const futureRef = useRef<GraphSnapshot[]>([]);
  const dragSnapshotRef = useRef<GraphSnapshot | null>(null);
  const seenEventsRef = useRef(new Set<number>());
  const [instance, setInstance] = useState<ReactFlowInstance<AgentFlowNode, Edge> | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [objective, setObjective] = useState("Analise o objetivo e produza uma entrega revisada pela equipe.");
  const [workspacePath, setWorkspacePath] = useState("");
  const [toast, setToast] = useState("");
  const [recentRuns, setRecentRuns] = useState<RunRecord[]>([]);
  const [runPanelOpen, setRunPanelOpen] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [runStatus, setRunStatus] = useState<RunStatus | null>(null);
  const [runEvents, setRunEvents] = useState<RunEvent[]>([]);
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [approvalNodeId, setApprovalNodeId] = useState<string | null>(null);

  useEffect(() => {
    const nextNodes = flowNodes(workflow.graph!);
    const nextEdges = flowEdges(workflow.graph!);
    setNodes(nextNodes);
    setEdges(nextEdges);
    nodesRef.current = nextNodes;
    edgesRef.current = nextEdges;
    pastRef.current = [];
    futureRef.current = [];
    setDirty(false);
    setSelectedNodeId(null);
    setPaletteOpen(false);
  }, [workflow.id]);

  const refreshRuns = useCallback(() => {
    api.runs().then((result) => setRecentRuns(result.runs)).catch(console.error);
  }, []);

  useEffect(() => refreshRuns(), [refreshRuns]);

  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2600);
  }

  function applySnapshot(snapshot: GraphSnapshot) {
    setNodes(snapshot.nodes);
    setEdges(snapshot.edges);
    nodesRef.current = snapshot.nodes;
    edgesRef.current = snapshot.edges;
  }

  function remember(snapshot = cloneSnapshot(nodesRef.current, edgesRef.current)) {
    pastRef.current = [...pastRef.current.slice(-79), snapshot];
    futureRef.current = [];
  }

  function commit(nextNodes: AgentFlowNode[], nextEdges: Edge[]) {
    remember();
    applySnapshot({ nodes: nextNodes, edges: nextEdges });
    setDirty(true);
  }

  function undo() {
    const previous = pastRef.current.at(-1);
    if (!previous) return;
    futureRef.current = [cloneSnapshot(nodesRef.current, edgesRef.current), ...futureRef.current];
    pastRef.current = pastRef.current.slice(0, -1);
    applySnapshot(previous);
    setDirty(true);
  }

  function redo() {
    const next = futureRef.current[0];
    if (!next) return;
    pastRef.current = [...pastRef.current, cloneSnapshot(nodesRef.current, edgesRef.current)];
    futureRef.current = futureRef.current.slice(1);
    applySnapshot(next);
    setDirty(true);
  }

  const onNodesChange = useCallback((changes: NodeChange<AgentFlowNode>[]) => {
    const removing = changes.some((change) => change.type === "remove");
    const next = applyNodeChanges(changes, nodesRef.current);
    if (removing) {
      const removedIds = new Set(changes.filter((change) => change.type === "remove").map((change) => change.id));
      commit(next, edgesRef.current.filter((edge) => !removedIds.has(edge.source) && !removedIds.has(edge.target)));
    } else {
      setNodes(next);
      nodesRef.current = next;
    }
  }, []);

  const onEdgesChange = useCallback((changes: EdgeChange<Edge>[]) => {
    const next = applyEdgeChanges(changes, edgesRef.current);
    if (changes.some((change) => change.type === "remove")) commit(nodesRef.current, next);
    else {
      setEdges(next);
      edgesRef.current = next;
    }
  }, []);

  const onConnect = useCallback((connection: Connection) => {
    const next = addEdge(
      {
        ...connection,
        id: `edge-${crypto.randomUUID()}`,
        type: "smoothstep",
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
      },
      edgesRef.current,
    );
    commit(nodesRef.current, next);
  }, []);

  function addNode(template: NodeTemplate, position?: { x: number; y: number }) {
    const id = `${template.kind}-${crypto.randomUUID().slice(0, 8)}`;
    const nextNode: AgentFlowNode = {
      id,
      type: "agentNode",
      position: position ?? { x: 420 + nodesRef.current.length * 18, y: 170 + (nodesRef.current.length % 4) * 110 },
      data: dataForTemplate(template),
    };
    commit([...nodesRef.current, nextNode], edgesRef.current);
    setSelectedNodeId(id);
  }

  function updateSelectedNode(data: WorkflowNodeData) {
    if (!selectedNodeId) return;
    const next = nodesRef.current.map((node) => node.id === selectedNodeId ? { ...node, data } : node);
    commit(next, edgesRef.current);
  }

  function duplicateSelectedNode() {
    const selected = nodesRef.current.find((node) => node.id === selectedNodeId);
    if (!selected) return;
    const id = `${selected.data.kind}-${crypto.randomUUID().slice(0, 8)}`;
    const duplicate: AgentFlowNode = {
      ...structuredClone(selected),
      id,
      selected: false,
      position: { x: selected.position.x + 32, y: selected.position.y + 32 },
      data: { ...selected.data, label: `${selected.data.label} cópia`, status: "idle" },
    };
    commit([...nodesRef.current, duplicate], edgesRef.current);
    setSelectedNodeId(id);
  }

  function deleteSelectedNode() {
    if (!selectedNodeId) return;
    commit(
      nodesRef.current.filter((node) => node.id !== selectedNodeId),
      edgesRef.current.filter((edge) => edge.source !== selectedNodeId && edge.target !== selectedNodeId),
    );
    setSelectedNodeId(null);
  }

  function clearSelection() {
    const next = nodesRef.current.map((node) => node.selected ? { ...node, selected: false } : node);
    setNodes(next);
    nodesRef.current = next;
    setSelectedNodeId(null);
  }

  function copilotCommand(command: string) {
    const normalized = command.toLowerCase();
    if (normalized.includes("econ") && normalized.includes("pesqui")) {
      const target = nodesRef.current.find((node) => node.data.label.toLowerCase().includes("pesquis"));
      if (!target) return "Não encontrei um agente de pesquisa neste grafo.";
      const next = nodesRef.current.map((node) =>
        node.id === target.id ? { ...node, data: { ...node.data, modelPolicy: "economical" as const } } : node,
      );
      commit(next, edgesRef.current);
      return "Política do Pesquisador alterada para Econômico.";
    }

    if (normalized.includes("revisor")) {
      if (nodesRef.current.some((node) => node.data.label === "Revisor final")) {
        return "O Revisor final já está antes da aprovação.";
      }
      const approval = nodesRef.current.find((node) => node.data.kind === "approval");
      if (!approval) return "Adicione uma etapa de aprovação para eu posicionar o revisor.";
      const id = `agent-${crypto.randomUUID().slice(0, 8)}`;
      const reviewer: AgentFlowNode = {
        id,
        type: "agentNode",
        position: { x: approval.position.x - 260, y: approval.position.y + 130 },
        data: {
          kind: "agent",
          label: "Revisor final",
          description: "Faz a última crítica antes da decisão",
          provider: "demo",
          modelPolicy: "deep",
          budget: 1,
          instructions: "Revise o conjunto final, aponte riscos e corrija inconsistências.",
          active: true,
          status: "idle",
        },
      };
      const incoming = edgesRef.current.filter((edge) => edge.target === approval.id);
      const otherEdges = edgesRef.current.filter((edge) => edge.target !== approval.id);
      const redirected = incoming.map((edge) => ({ ...edge, id: `${edge.id}-review`, target: id }));
      const finalEdge: Edge = {
        id: `edge-${id}-${approval.id}`,
        source: id,
        target: approval.id,
        type: "smoothstep",
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
      };
      commit([...nodesRef.current, reviewer], [...otherEdges, ...redirected, finalEdge]);
      return "Revisor final criado e conectado antes da aprovação humana.";
    }
    return "Entendi o pedido. Nesta versão posso adicionar revisores e ajustar a política do Pesquisador; o roteador semântico entra no próximo conector de IA.";
  }

  async function saveWorkflow() {
    setSaving(true);
    try {
      const result = await api.saveWorkflow(workflow.id, workflow.name, cleanGraph(nodesRef.current, edgesRef.current));
      onWorkflowUpdate(result.workflow);
      setDirty(false);
      showToast("Alterações salvas");
      return result.workflow;
    } finally {
      setSaving(false);
    }
  }

  async function publishWorkflow() {
    setPublishing(true);
    try {
      await saveWorkflow();
      const result = await api.publishWorkflow(workflow.id);
      onWorkflowUpdate(result.workflow);
      setPublishOpen(false);
      showToast(`Versão ${result.workflow.version} publicada`);
    } finally {
      setPublishing(false);
    }
  }

  function resetNodeStatuses() {
    const next = nodesRef.current.map((node) => ({ ...node, data: { ...node.data, status: "idle" as NodeStatus } }));
    setNodes(next);
    nodesRef.current = next;
  }

  async function startRun() {
    setSaving(true);
    try {
      const saved = await api.saveWorkflow(workflow.id, workflow.name, cleanGraph(nodesRef.current, edgesRef.current));
      onWorkflowUpdate(saved.workflow);
      const result = await api.runWorkflow(workflow.id, {
        objective,
        ...(workspacePath.trim() ? { workspacePath: workspacePath.trim() } : {}),
      });
      resetNodeStatuses();
      seenEventsRef.current = new Set();
      setRunEvents([]);
      setApprovalNodeId(null);
      setActiveRunId(result.runId);
      setRunStatus("queued");
      setRunStartedAt(Date.now());
      setRunPanelOpen(true);
      setTestOpen(false);
      setDirty(false);
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    if (!activeRunId) return;
    return subscribeToRun(activeRunId, (event) => {
      if (seenEventsRef.current.has(event.id)) return;
      seenEventsRef.current.add(event.id);
      setRunEvents((current) => [...current, event]);

      if (event.nodeId && event.type.startsWith("node.")) {
        const status: NodeStatus = event.type === "node.running"
          ? "running"
          : event.type === "node.completed"
            ? "completed"
            : "error";
        const next = nodesRef.current.map((node) =>
          node.id === event.nodeId ? { ...node, data: { ...node.data, status } } : node,
        );
        setNodes(next);
        nodesRef.current = next;
      }
      if (event.type === "approval.required" && event.nodeId) {
        setApprovalNodeId(event.nodeId);
        setRunStatus("waiting_approval");
        const next = nodesRef.current.map((node) =>
          node.id === event.nodeId ? { ...node, data: { ...node.data, status: "waiting" as NodeStatus } } : node,
        );
        setNodes(next);
        nodesRef.current = next;
      }
      if (event.type === "approval.approved" && event.nodeId) {
        setApprovalNodeId(null);
        setRunStatus("running");
        const next = nodesRef.current.map((node) =>
          node.id === event.nodeId ? { ...node, data: { ...node.data, status: "completed" as NodeStatus } } : node,
        );
        setNodes(next);
        nodesRef.current = next;
      }
      if (event.type === "run.started") setRunStatus("running");
      if (event.type === "run.waiting") setRunStatus("waiting_approval");
      if (event.type === "run.completed") {
        setRunStatus("completed");
        refreshRuns();
      }
      if (event.type === "run.failed") {
        setRunStatus("failed");
        refreshRuns();
      }
      if (event.type === "run.cancelled") {
        setRunStatus("cancelled");
        refreshRuns();
      }
    });
  }, [activeRunId, refreshRuns]);

  async function approve(decision: "approved" | "rejected") {
    if (!activeRunId || !approvalNodeId) return;
    await api.resolveApproval(activeRunId, approvalNodeId, decision);
  }

  async function cancel() {
    if (!activeRunId) return;
    await api.cancelRun(activeRunId);
  }

  const selectedNode = nodes.find((node) => node.id === selectedNodeId) ?? null;
  const runActive = runStatus === "queued" || runStatus === "running" || runStatus === "waiting_approval";

  return (
    <div className={`builder-shell ${runPanelOpen ? "has-open-run" : ""}`}>
      <header className="builder-topbar">
        <button
          className="icon-button mobile-palette-toggle"
          aria-label={paletteOpen ? "Fechar biblioteca de nós" : "Abrir biblioteca de nós"}
          title={paletteOpen ? "Fechar biblioteca de nós" : "Abrir biblioteca de nós"}
          onClick={() => setPaletteOpen((current) => !current)}
        >
          <PanelLeft size={16} />
        </button>
        <div className="builder-breadcrumb">
          <span>Workflows</span><ChevronRight size={13} />
          <strong>{workflow.name}</strong>
          <span className="version-label">v{workflow.version || "rascunho"}</span>
        </div>
        <div className="save-state">
          <span className={`status-dot ${dirty ? "status-dot--dirty" : "status-dot--ok"}`} />
          {dirty ? "Alterações não salvas" : "Salvo"}
        </div>
        <div className="builder-toolbar">
          <button className="environment-button"><span className="status-dot status-dot--ok" />Produção</button>
          <span className="toolbar-divider" />
          <button className="icon-button" title="Desfazer" aria-label="Desfazer" onClick={undo} disabled={pastRef.current.length === 0}><Undo2 size={16} /></button>
          <button className="icon-button" title="Refazer" aria-label="Refazer" onClick={redo} disabled={futureRef.current.length === 0}><Redo2 size={16} /></button>
          <button className="button button--secondary" onClick={saveWorkflow} disabled={!dirty || saving}>
            {saving ? <LoaderCircle className="spin" size={15} /> : <Save size={15} />}Salvar
          </button>
          <button className="button button--secondary" onClick={() => setTestOpen(true)} disabled={runActive}>
            <Play size={15} />Testar
          </button>
          <button className="button button--primary" onClick={() => setPublishOpen(true)}>
            <CloudUpload size={15} />Publicar
          </button>
        </div>
      </header>

      <div className="builder-body">
        <NodePalette
          open={paletteOpen}
          onAdd={(template) => {
            addNode(template);
            setPaletteOpen(false);
          }}
          recentRuns={recentRuns}
        />
        <main
          className="flow-canvas"
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
          }}
          onDrop={(event) => {
            event.preventDefault();
            const raw = event.dataTransfer.getData("application/go-win-node");
            if (!raw || !instance) return;
            const template = JSON.parse(raw) as NodeTemplate;
            addNode(template, instance.screenToFlowPosition({ x: event.clientX, y: event.clientY }));
          }}
        >
          <ReactFlow<AgentFlowNode, Edge>
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setInstance}
            onPaneClick={clearSelection}
            onSelectionChange={({ nodes: selection }) => setSelectedNodeId(selection[0]?.id ?? null)}
            onNodeDragStart={() => { dragSnapshotRef.current = cloneSnapshot(nodesRef.current, edgesRef.current); }}
            onNodeDragStop={() => {
              if (dragSnapshotRef.current) remember(dragSnapshotRef.current);
              dragSnapshotRef.current = null;
              setDirty(true);
            }}
            defaultViewport={{ x: 24, y: 48, zoom: 0.7 }}
            minZoom={0.25}
            maxZoom={1.8}
            snapToGrid
            snapGrid={[16, 16]}
            defaultEdgeOptions={{ type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed } }}
            deleteKeyCode={["Backspace", "Delete"]}
          >
            <Background variant={BackgroundVariant.Dots} gap={18} size={1.2} />
            <MiniMap pannable zoomable nodeStrokeWidth={3} />
            <Controls showInteractive={false} />
          </ReactFlow>
          <div className="canvas-caption"><span className="status-dot status-dot--ok" />Canvas conectado ao runtime</div>
        </main>
        <Inspector
          node={selectedNode}
          onChange={updateSelectedNode}
          onDuplicate={duplicateSelectedNode}
          onDelete={deleteSelectedNode}
          onCopilotCommand={copilotCommand}
          onClose={clearSelection}
        />
      </div>

      <RunPanel
        open={runPanelOpen}
        onOpenChange={setRunPanelOpen}
        runId={activeRunId}
        status={runStatus}
        events={runEvents}
        startedAt={runStartedAt}
        approvalNodeId={approvalNodeId}
        onApprove={() => void approve("approved")}
        onReject={() => void approve("rejected")}
        onCancel={() => void cancel()}
      />

      {testOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setTestOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="test-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Execução de teste</p><h2 id="test-title">Defina o objetivo</h2></div><button className="icon-button" aria-label="Fechar" onClick={() => setTestOpen(false)}><X size={17} /></button></div>
            <label><span>Entrada do orquestrador</span><textarea rows={5} value={objective} onChange={(event) => setObjective(event.target.value)} autoFocus /></label>
            <label>
              <span><FolderOpen size={14} />Pasta do caso</span>
              <input
                value={workspacePath}
                onChange={(event) => setWorkspacePath(event.target.value)}
                placeholder="Caminho local ou link da pasta no Google Drive"
                spellCheck={false}
              />
            </label>
            <div className="modal-note">O teste usa o rascunho atual e registra todos os eventos.</div>
            <div className="modal-actions"><button className="button button--secondary" onClick={() => setTestOpen(false)}>Cancelar</button><button className="button button--primary" onClick={() => void startRun()} disabled={!objective.trim() || saving}><Play size={15} />Iniciar teste</button></div>
          </div>
        </div>
      )}

      {publishOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setPublishOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="publish-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="modal-header"><div><p className="eyebrow">Produção</p><h2 id="publish-title">Publicar nova versão</h2></div><button className="icon-button" aria-label="Fechar" onClick={() => setPublishOpen(false)}><X size={17} /></button></div>
            <div className="publish-summary"><div><span>Workflow</span><strong>{workflow.name}</strong></div><div><span>Próxima versão</span><strong>v{workflow.version + 1}</strong></div><div><span>Grafo</span><strong>{nodes.length} nós · {edges.length} conexões</strong></div></div>
            <p className="modal-note">O webhook de produção passará a usar esta versão. O rascunho continua editável.</p>
            <div className="modal-actions"><button className="button button--secondary" onClick={() => setPublishOpen(false)}>Cancelar</button><button className="button button--primary" onClick={() => void publishWorkflow()} disabled={publishing}>{publishing ? <LoaderCircle className="spin" size={15} /> : <CloudUpload size={15} />}Publicar</button></div>
          </div>
        </div>
      )}

      {toast && <div className="toast"><Check size={15} />{toast}</div>}
    </div>
  );
}
